import { PlusOne, Search, SearchOff } from "@mui/icons-material";
import CachedIcon from "@mui/icons-material/Cached";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import FilterListIcon from "@mui/icons-material/FilterList";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import MapIcon from "@mui/icons-material/Map";
import {
  Alert,
  Badge,
  Box,
  Chip,
  CircularProgress,
  IconButton,
  InputAdornment,
  Paper,
  Popover,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Toolbar,
  Tooltip,
  Typography
} from "@mui/material";
import debounce from "lodash.debounce";
import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { useAccountPermissions } from "../../hooks/Permissions/useAccountPermissions";
import { CaptureResponse } from "../../types/model/capture";
import { Entity } from "../../types/model/entity/Entity";
import { ManagementUnit } from "../../types/model/managementUnit";
import GenericFilter, { FilterField } from "../General/GenericFilter";
import { CapturesImportDialog } from "../Imports/Captures/CapturesImportDialog";
import { useEntityAccessPermissions } from "../../hooks/Permissions/useEntityAccessPermissions";

interface FilterOptions {
  entityId?: number;
  managementUnitId?: number;
  militaryMap?: string;
  search?: string;
}

interface CapturesTableProps {
  tableData: CaptureResponse[];
  onRequestRefresh: () => void;
  onApplyFilters?: (filter: FilterOptions) => void;
  isMapOpen: boolean;
  toggleOpenMap: () => void;

  entityId?: number;
  managementUnitId?: number;

  entities?: Entity[];
  allManagementUnits?: ManagementUnit[];
  selectedCaptures: number[];
  setSelectedCaptures: React.Dispatch<React.SetStateAction<number[]>>;
  mapHasData: boolean;
  page: number;
  rowsPerPage: number;
  totalCapturesCount: number;
  onPageChange: (event: unknown, newPage: number) => void;
  onRowsPerPageChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  isLoading: boolean;
}

const CapturesTable = ({
  tableData,
  entities = [],
  allManagementUnits = [],
  onRequestRefresh,
  onApplyFilters,
  isMapOpen,
  toggleOpenMap,
  entityId,
  managementUnitId,
  selectedCaptures,
  setSelectedCaptures,
  mapHasData,
  page,
  rowsPerPage,
  totalCapturesCount,
  onPageChange,
  onRowsPerPageChange,
  isLoading,
}: CapturesTableProps) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  
  const { canEditData } = useAccountPermissions();
  const {
    canEditEntity,
    isLoading: isLoadingEntityPermissions,
  } = useEntityAccessPermissions();

  const isViewingSpecificEntityOrUnit = entityId !== undefined || managementUnitId !== undefined;

  const canEdit =
    canEditData &&
    (!isViewingSpecificEntityOrUnit ||
      (!isLoadingEntityPermissions && entityId !== undefined && canEditEntity(entityId)));

  const [filterAnchorEl, setFilterAnchorEl] = React.useState<HTMLButtonElement | null>(null);
  const [filters, setFilters] = React.useState<FilterOptions>({});
  const [openImportDialog, setOpenImportDialog] = React.useState(false);

  const filterFields: FilterField[] = useMemo(() => {
    if (managementUnitId) {
      return [
        {
          name: "militaryMap",
          label: t("captures.capture.general.identification.military-map"),
          type: "text",
        },
      ];
    };

    const filteredUnits = filters.entityId
      ? allManagementUnits.filter(u => u.entityId === filters.entityId)
      : allManagementUnits;

    if (entityId) {
      return [
        {
          name: "managementUnitId",
          label: t("captures.capture.general.identification.management-unit"),
          type: "autocomplete",
          options: filteredUnits
            .filter(u => u.entityId === entityId)
            .map(u => ({ value: u.id, label: u.name })),
        },
        {
          name: "militaryMap",
          label: t("captures.capture.general.identification.military-map"),
          type: "text",
        },
      ];
    }

    return [
      {
        name: "entityId",
        label: t("captures.capture.general.identification.entity"),
        type: "autocomplete",
        options: (entities || []).map((e) => ({
          value: e.id,
          label: e.name?.toUpperCase(),
        })),
      },
      {
        name: "managementUnitId",
        label: t("captures.capture.general.identification.management-unit"),
        type: "autocomplete",
        options: filteredUnits.map(u => ({ value: u.id, label: u.name })),
      },
      {
        name: "militaryMap",
        label: t("captures.capture.general.identification.military-map"),
        type: "text",
      },
    ];
  }, [entities, allManagementUnits, entityId, managementUnitId, filters.entityId, t]);

  const handleClick = (id: number) => {
    navigate(`/captures/${id}`);
  };

  const handleSelectClick = (event: React.MouseEvent, id: number) => {
    event.stopPropagation(); // Prevent row click from firing

    if (selectedCaptures.includes(id)) {
      setSelectedCaptures(selectedCaptures.filter((item) => item !== id));
    } else {
      setSelectedCaptures([...selectedCaptures, id]);
    }
  };

  const handleToggleFilter = (event: React.MouseEvent<HTMLButtonElement>) => {
    setFilterAnchorEl(filterAnchorEl ? null : event.currentTarget);
  };

  const handleCloseFilter = () => {
    setFilterAnchorEl(null);
  };

  const isFilterOpen = Boolean(filterAnchorEl);

  const handleFiltersChange = (newFilters: FilterOptions) => {
    if (newFilters.entityId !== filters.entityId) {
      newFilters.managementUnitId = undefined;
    }
    setFilters(newFilters);
  };

  const handleApplyFilters = React.useCallback((appliedFilters?: FilterOptions) => {
    if (!onApplyFilters) return;

    onApplyFilters(appliedFilters ?? filters);
  }, [onApplyFilters, filters]);

  const onApplyFiltersRef = React.useRef(handleApplyFilters);
  onApplyFiltersRef.current = handleApplyFilters;

  const debouncedApplyFilters = React.useMemo(
    () => debounce(() => onApplyFiltersRef.current(), 1000),
    []
  );

  const handleClearFilters = () => {
    setFilters({});
    handleApplyFilters({});
    onPageChange(null, 0);
  };

  const handleDeleteFilter = (key: keyof FilterOptions) => {
    const newFilters = { ...filters, [key]: undefined };
    setFilters(newFilters);
    handleApplyFilters(newFilters);
  };

  const handleRequestAdd = () => {
    const params = new URLSearchParams();

    if (entityId) params.set("entityId", entityId.toString());
    if (managementUnitId) params.set("managementUnitId", managementUnitId.toString());

    navigate(`/captures/add-capture?${params.toString()}`);
  };

  React.useEffect(() => {
    if (filters.search !== undefined) {
      debouncedApplyFilters();
    }
    return () => debouncedApplyFilters.cancel();
  }, [filters.search, debouncedApplyFilters]);

  const activeFilters = useMemo(() => {
    const list: { key: keyof FilterOptions; label: string; value: string }[] = [];
    if (filters.entityId) {
      const entity = entities.find(e => e.id === filters.entityId);
      if (entity) {
        list.push({
          key: "entityId",
          label: t("captures.capture.general.identification.entity"),
          value: entity.name?.toUpperCase(),
        });
      }
    }
    if (filters.managementUnitId) {
      const unit = allManagementUnits.find(u => u.id === filters.managementUnitId);
      if (unit) list.push({ key: "managementUnitId", label: t("captures.capture.general.identification.management-unit"), value: unit.name });
    }
    if (filters.militaryMap) {
      list.push({ key: "militaryMap", label: t("captures.capture.general.identification.military-map"), value: filters.militaryMap });
    }
    return list;
  }, [filters, entities, allManagementUnits, t]);

  const tableContent = useMemo(() => {
    if (isLoading) {
      return (
        <TableRow>
          <TableCell colSpan={7} align="center">
            <CircularProgress />
          </TableCell>
        </TableRow>
      );
    }

    if (tableData.length === 0) {
      return (
        <TableRow>
          <TableCell colSpan={7} align="center" sx={{ py: 10 }}>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                opacity: 0.5,
              }}
            >
              <SearchOff sx={{ fontSize: 64, mb: 2 }} />
              <Typography variant="h6">{t("captures.capture.no-captures")}</Typography>
            </Box>
          </TableCell>
        </TableRow>
      );
    }

    return tableData.map((row) => {
      const isItemSelected = selectedCaptures.includes(row.id);
      return (
        <TableRow
          hover
          key={row.id}
          onClick={() => handleClick(row.id)}
          selected={isItemSelected}
          sx={{ cursor: "pointer" }}
        >
          <TableCell>{row.name}</TableCell>
          <TableCell>{row.managementUnitName}</TableCell>
          <TableCell>{row.entityAcronym}</TableCell>
          <TableCell>{row.aquaEpsDesignation}</TableCell>
          <TableCell>{row.entityDesignation}</TableCell>
          <TableCell>
            {row.licensingDesignation &&
              `${row.licensingDesignationSystem ? `${row.licensingDesignationSystem}: ` : ""}${row.licensingDesignation}`}
          </TableCell>
          <TableCell padding="checkbox" align="center">
            <Tooltip title={mapHasData ? "" : t("captures.map.apply-filter-to-select")}>
              <span>
                <IconButton
                  color={isItemSelected ? "success" : "default"}
                  onClick={(event) => {
                    event.stopPropagation();
                    if (!mapHasData) return;
                    handleSelectClick(event, row.id);
                  }}
                  sx={{
                    opacity: mapHasData ? 1 : 0.5,
                    pointerEvents: "auto",
                  }}
                >
                  <LocationOnIcon />
                </IconButton>
              </span>
            </Tooltip>
          </TableCell>
        </TableRow>
      );
    });
  }, [isLoading, tableData, selectedCaptures, mapHasData, t]);

  return (
    <Box
      sx={{
        width: "100%",
        height: "100%",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Paper
        sx={{
          width: "100%",
          height: "100%",
          display: "flex",
          flexDirection: "column",
          p: 2,
        }}
      >
        <Toolbar>
          <Typography sx={{ flex: "1 1 100%" }} variant="h5" component="div">
            {t("captures.capture.title")}
          </Typography>

          <Tooltip title={t("generic.open")}>
            <IconButton onClick={toggleOpenMap} disabled={isMapOpen && !mapHasData} sx={{ ml: 2 }}>
              <MapIcon color={isMapOpen ? "primary" : "secondary"} />
            </IconButton>
          </Tooltip>

          {filterFields.length > 0 && (
            <Tooltip title={t("generic.filter")}>
              <IconButton onClick={handleToggleFilter}>
                <Badge badgeContent={activeFilters.length} color="primary">
                  <FilterListIcon color={isFilterOpen ? "primary" : undefined} />
                </Badge>
              </IconButton>
            </Tooltip>
          )}

          <Tooltip title={t("generic.refresh")}>
            <IconButton onClick={onRequestRefresh}>
              <CachedIcon />
            </IconButton>
          </Tooltip>

          {canEdit && (
            <>
              <Tooltip title={t("generic.add")}>
                <IconButton onClick={handleRequestAdd}>
                  <PlusOne />
                </IconButton>
              </Tooltip>

              <Tooltip title={t("imports.import-dialog.title")}>
                <IconButton onClick={() => setOpenImportDialog(true)}>
                  <CloudUploadIcon />
                </IconButton>
              </Tooltip>
            </>
          )}
        </Toolbar>

        <TextField
          variant="outlined"
          size="small"
          placeholder={t("captures.capture.search")}
          value={filters.search || ""}
          onChange={(e) => handleFiltersChange({ ...filters, search: e.target.value })}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              handleApplyFilters();
            }
          }}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              ),
            },
          }}
          sx={{ mb: 2 }}
        />

        {!mapHasData && (
          <Alert severity="info" sx={{ mb: 2 }}>
            {t("captures.capture.map-requires-filter")}
          </Alert>
        )}

        {activeFilters.length > 0 && (
          <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: "wrap", gap: 1 }}>
            {activeFilters.map((filter) => (
              <Chip
                key={filter.key}
                label={`${filter.label}: ${filter.value}`}
                onDelete={() => handleDeleteFilter(filter.key)}
                size="small"
                variant="outlined"
              />
            ))}
          </Stack>
        )}

        <Popover
          open={isFilterOpen}
          anchorEl={filterAnchorEl}
          onClose={handleCloseFilter}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "center",
          }}
          transformOrigin={{
            vertical: "top",
            horizontal: "center",
          }}
          slotProps={{
            paper: {
              sx: { p: 3, width: 350 }
            }
          }}
        >
          <GenericFilter
            fields={filterFields}
            filters={filters}
            onFiltersChange={handleFiltersChange}
            onApplyFilters={handleApplyFilters}
            onClearFilters={handleClearFilters}
          />
        </Popover>

        <TableContainer
          sx={{
            flex: 1,
            overflow: "auto",
            minHeight: 0,
          }}
        >
          <Table stickyHeader size="small" sx={{ minWidth: 750 }}>
            <TableHead>
              <TableRow>
                <TableCell>{t("captures.capture.general.identification.name")}</TableCell>
                <TableCell>{t("captures.capture.general.management-unit.name")}</TableCell>
                <TableCell>{t("captures.capture.general.identification.entity-acronym")}</TableCell>
                <TableCell>{t("captures.capture.general.identification.aquaEsp-desig")}</TableCell>
                <TableCell>{t("captures.capture.general.identification.entity-desig")}</TableCell>
                <TableCell>{t("captures.capture.general.identification.licensing-desig")}</TableCell>
                <TableCell>{t("generic.select")}</TableCell>

              </TableRow>
            </TableHead>
            <TableBody>
              {tableContent}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          rowsPerPageOptions={[25, 50, 100]}
          component="div"
          count={totalCapturesCount}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={onPageChange}
          onRowsPerPageChange={onRowsPerPageChange}
        />
      </Paper>
      <CapturesImportDialog
        open={openImportDialog}
        onClose={() => setOpenImportDialog(false)}
        onSuccess={() => {
          setOpenImportDialog(false);
          onRequestRefresh();
        }}
        entityId={entityId}
        managementUnitId={managementUnitId}
      />
    </Box>
  );
};

export default CapturesTable;